import { Button, Textarea, useDialog } from '@emailforge/ui';
import { useState } from 'react';

import { Icon } from '@/components/ui/icons';
import {
  type CommentRow,
  useCreateCommentMutation,
  useDeleteCommentMutation,
  useListCommentsQuery,
  useReopenCommentMutation,
  useResolveCommentMutation,
} from '@/features/collab/collabApi';

type Props = {
  versionId: string;
  selectedBlockId: string | null;
  currentUserId: string;
};

export function CommentsPanel({ versionId, selectedBlockId, currentUserId }: Props) {
  const { data: comments, isLoading } = useListCommentsQuery(versionId, { pollingInterval: 10_000 });
  const [createComment, { isLoading: isCreating }] = useCreateCommentMutation();
  const [resolveComment] = useResolveCommentMutation();
  const [reopenComment] = useReopenCommentMutation();
  const [deleteComment] = useDeleteCommentMutation();
  const [showResolved, setShowResolved] = useState(false);
  const [body, setBody] = useState('');

  const filtered = (comments ?? []).filter((c) =>
    selectedBlockId ? c.blockId === selectedBlockId : true,
  );
  const visibleAll = filtered.filter((c) => (showResolved ? true : !c.resolvedAt));
  const topLevel = visibleAll.filter((c) => !c.parentId);
  const repliesByParent = new Map<string, CommentRow[]>();
  for (const c of visibleAll) {
    if (c.parentId) {
      const arr = repliesByParent.get(c.parentId) ?? [];
      arr.push(c);
      repliesByParent.set(c.parentId, arr);
    }
  }

  const submit = async () => {
    if (!selectedBlockId || !body.trim()) return;
    const mentions = parseMentions(body);
    await createComment({
      versionId,
      blockId: selectedBlockId,
      body: body.trim(),
      mentions: mentions.length > 0 ? mentions : undefined,
    }).unwrap();
    setBody('');
  };

  const submitReply = async (parent: CommentRow, replyBody: string) => {
    const mentions = parseMentions(replyBody);
    await createComment({
      versionId,
      blockId: parent.blockId,
      parentId: parent.id,
      body: replyBody.trim(),
      mentions: mentions.length > 0 ? mentions : undefined,
    }).unwrap();
  };

  return (
    <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <div
          className="mono"
          style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.08, color: 'var(--ink-3)' }}
        >
          Comments
        </div>
        <div style={{ flex: 1 }} />
        <label style={{ fontSize: 11, color: 'var(--ink-3)', display: 'flex', alignItems: 'center', gap: 4 }}>
          <input
            type="checkbox"
            checked={showResolved}
            onChange={(e) => setShowResolved(e.target.checked)}
          />
          Show resolved
        </label>
      </div>

      {!selectedBlockId && (
        <div
          style={{
            padding: 16,
            textAlign: 'center',
            background: 'var(--paper-2)',
            borderRadius: 'var(--r-2)',
            fontSize: 12.5,
            color: 'var(--ink-3)',
          }}
        >
          Select a block to view or post comments anchored to it.
        </div>
      )}

      {selectedBlockId && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {isLoading && (
            <div className="mono" style={{ fontSize: 11, color: 'var(--ink-3)' }}>
              Loading…
            </div>
          )}

          {topLevel.length === 0 && !isLoading && (
            <div style={{ fontSize: 12, color: 'var(--ink-3)' }}>
              No comments on this block yet.
            </div>
          )}

          {topLevel.map((c) => (
            <CommentThread
              key={c.id}
              comment={c}
              replies={repliesByParent.get(c.id) ?? []}
              currentUserId={currentUserId}
              onResolve={() => resolveComment({ versionId, id: c.id })}
              onReopen={() => reopenComment({ versionId, id: c.id })}
              onDelete={() => deleteComment({ versionId, id: c.id })}
              onReply={(replyBody) => submitReply(c, replyBody)}
            />
          ))}

          <Textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="Add a comment… use @email to mention a teammate"
            style={{ height: 80, padding: 8, fontSize: 13, resize: 'vertical' }} />
          <Button variant="flame" size="sm" onClick={submit} disabled={!body.trim() || isCreating}>
            {isCreating ? 'Posting…' : 'Post comment'}
          </Button>
        </div>
      )}
    </div>
  );
}

function CommentThread({
  comment,
  replies,
  currentUserId,
  onResolve,
  onReopen,
  onDelete,
  onReply,
}: {
  comment: CommentRow;
  replies: CommentRow[];
  currentUserId: string;
  onResolve: () => void;
  onReopen: () => void;
  onDelete: () => void;
  onReply: (body: string) => Promise<void> | void;
}) {
  const [replyOpen, setReplyOpen] = useState(false);
  const [replyBody, setReplyBody] = useState('');
  const [posting, setPosting] = useState(false);

  const submitReply = async () => {
    if (!replyBody.trim()) return;
    setPosting(true);
    try {
      await onReply(replyBody.trim());
      setReplyBody('');
      setReplyOpen(false);
    } finally {
      setPosting(false);
    }
  };

  return (
    <div
      className="mf-card"
      style={{
        padding: 10,
        background: comment.resolvedAt ? 'var(--paper-2)' : 'var(--paper)',
        opacity: comment.resolvedAt ? 0.7 : 1,
      }}
    >
      <CommentBody comment={comment} currentUserId={currentUserId} onDelete={onDelete} />

      {replies.length > 0 && (
        <div
          style={{
            marginTop: 10,
            paddingLeft: 12,
            borderLeft: '2px solid var(--line-soft)',
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
          }}
        >
          {replies
            .slice()
            .sort((a, b) => a.createdAt.localeCompare(b.createdAt))
            .map((r) => (
              <CommentBody
                key={r.id}
                comment={r}
                currentUserId={currentUserId}
                onDelete={() => undefined}
                compact
              />
            ))}
        </div>
      )}

      <div style={{ display: 'flex', gap: 4, marginTop: 8, justifyContent: 'flex-end' }}>
        {!comment.resolvedAt && !replyOpen && (
          <Button variant="ghost" size="sm" onClick={() => setReplyOpen(true)}>
            Reply
          </Button>
        )}
        {!comment.resolvedAt ? (
          <Button variant="ghost" size="sm" onClick={onResolve}>
            <Icon.check size={11} /> Resolve
          </Button>
        ) : (
          <Button variant="ghost" size="sm" onClick={onReopen}>
            Reopen
          </Button>
        )}
      </div>

      {replyOpen && (
        <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 6 }}>
          <Textarea
            value={replyBody}
            onChange={(e) => setReplyBody(e.target.value)}
            placeholder="Reply… use @email to mention"
            style={{ height: 60, padding: 8, fontSize: 12.5, resize: 'vertical' }}
          />
          <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setReplyOpen(false);
                setReplyBody('');
              }}
            >
              Cancel
            </Button>
            <Button variant="flame" size="sm" onClick={submitReply} disabled={!replyBody.trim() || posting}>
              {posting ? 'Posting…' : 'Post reply'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function CommentBody({
  comment,
  currentUserId,
  onDelete,
  compact = false,
}: {
  comment: CommentRow;
  currentUserId: string;
  onDelete: () => void;
  compact?: boolean;
}) {
  const isOwn = comment.authorId === currentUserId;
  const { confirm } = useDialog();
  const initials = (comment.author?.name ?? comment.author?.email ?? 'U')
    .split(' ')
    .map((p) => p[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
        <div
          style={{
            width: compact ? 18 : 22,
            height: compact ? 18 : 22,
            borderRadius: '50%',
            background: 'oklch(0.72 0.09 45)',
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: compact ? 8.5 : 9.5,
            fontWeight: 600,
          }}
        >
          {initials}
        </div>
        <div style={{ fontSize: compact ? 11.5 : 12, fontWeight: 550, flex: 1 }}>
          {comment.author?.name ?? comment.author?.email ?? 'Someone'}
        </div>
        <span className="mono" style={{ fontSize: 10, color: 'var(--ink-3)' }}>
          {timeago(comment.createdAt)}
        </span>
        {isOwn && (
          <button
            onClick={async () => {
              if (await confirm({
                title: 'Delete comment?',
                description: 'This thread keeps the resolved-state of the conversation but the body will be removed.',
                confirmLabel: 'Delete',
                destructive: true,
              })) onDelete();
            }}
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              color: 'var(--ink-3)',
              padding: 2,
            }}
            aria-label="Delete"
          >
            <Icon.trash size={11} />
          </button>
        )}
      </div>
      <div
        style={{
          fontSize: compact ? 12.5 : 13,
          color: 'var(--ink-2)',
          lineHeight: 1.5,
          whiteSpace: 'pre-wrap',
        }}
      >
        {renderBodyWithMentions(comment.body)}
      </div>
    </div>
  );
}

function parseMentions(body: string): string[] {
  const out = new Set<string>();
  const re = /@([\w.+-]+@[\w.-]+\.[a-z]{2,})/gi;
  let m: RegExpExecArray | null;
  while ((m = re.exec(body)) !== null) {
    if (m[1]) out.add(m[1]);
  }
  return [...out];
}

function renderBodyWithMentions(body: string): React.ReactNode {
  const parts: React.ReactNode[] = [];
  const re = /@([\w.+-]+@[\w.-]+\.[a-z]{2,})/gi;
  let lastIndex = 0;
  let m: RegExpExecArray | null;
  let i = 0;
  while ((m = re.exec(body)) !== null) {
    if (m.index > lastIndex) parts.push(body.slice(lastIndex, m.index));
    parts.push(
      <span
        key={`m-${i++}`}
        style={{
          background: 'var(--flame-soft)',
          color: 'var(--flame-2)',
          padding: '0 4px',
          borderRadius: 3,
          fontWeight: 500,
        }}
      >
        @{m[1]}
      </span>,
    );
    lastIndex = m.index + m[0].length;
  }
  if (lastIndex < body.length) parts.push(body.slice(lastIndex));
  return parts.length === 0 ? body : parts;
}

function timeago(iso: string): string {
  const seconds = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}
